<script setup lang="ts">
  import { computed } from 'vue';

  import type { Task } from '@stuut/api/models';
  import { Badge } from '@ui/components/badge';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import PriorityIndicator from '@/components/common/PriorityIndicator.vue';

  interface Props {
    showSuffix?: boolean;
    size?: '2xs' | 'xs' | 'sm';
    task: Task;
    urgentAlwaysBadge?: boolean;
    variant?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 'sm',
  });

  const badgeClassNames = {
    high: 'text-error border-error',
    low: 'text-muted-foreground border-border',
    medium: 'text-warning-700 border-warning-800',
  };

  const isBadge = computed(() => {
    return props.variant?.toLowerCase() === 'badge';
  });

  const isIndicator = computed(() => {
    return props.variant?.toLowerCase() === 'indicator';
  });

  const badgeClass = computed(() => {
    return badgeClassNames?.[props.task?.priority];
  });

  const showUrgentBadge = computed(() => {
    return props.urgentAlwaysBadge && props.task?.isUrgent;
  });

  const badgeVariant = computed(() => {
    switch (props.task?.priority) {
      case 'urgent':
        return 'priority-urgent';
      default:
        return 'outline';
    }
  });

  const showBadge = computed(() => {
    return isBadge.value || showUrgentBadge.value || !isIndicator.value;
  });
</script>

<template>
  <Badge v-if="showBadge" :class="badgeClass" :variant="badgeVariant" :size="size">
    {{ task.priority }}

    <template v-if="showSuffix && !['urgent'].includes(task.priority)"> priority </template>
  </Badge>

  <Tooltip v-else-if="isIndicator">
    <TooltipTrigger as-child>
      <PriorityIndicator :priority="task.priority" :size="size" />
    </TooltipTrigger>

    <TooltipContent class="text-xs">
      {{ task.priority }}

      <template v-if="!['urgent'].includes(task.priority)"> priority </template>
    </TooltipContent>
  </Tooltip>
</template>

<style scoped></style>
